import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { 
  fetchAnalysisAsync,
  clearError, 
  clearMessage,
  clearAnalysisData,
  setAnalysisParams
} from '../features/analysis/analysis.slice';
import type { RootState, AppDispatch } from '../store';
import type { AnalysisParams } from '../api/analysis';

export const useAnalysis = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  const {
    analysisData,
    loading,
    error,
    message,
    currentParams
  } = useSelector((state: RootState) => state.analysis);

  // Fetch analysis data
  const fetchAnalysis = useCallback((params?: AnalysisParams) => {
    if (params) {
      dispatch(setAnalysisParams(params));
      return dispatch(fetchAnalysisAsync(params));
    } else {
      return dispatch(fetchAnalysisAsync(currentParams));
    }
  }, [dispatch]); // Remove currentParams from dependency array

  // Clear error
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear message
  const handleClearMessage = useCallback(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  // Clear all analysis data
  const handleClearAnalysisData = useCallback(() => {
    dispatch(clearAnalysisData());
  }, [dispatch]);

  // Refresh data with current params
  const refreshAnalysis = useCallback(() => {
    return dispatch(fetchAnalysisAsync(currentParams));
  }, [dispatch]); // Remove currentParams from dependency array

  // Set years and fetch data
  const setYears = useCallback((year1: number, year2: number) => {
    const params = { year1, year2 };
    return dispatch(fetchAnalysisAsync(params));
  }, [dispatch]);

  // Format currency values
  const formatAmount = useCallback((amount: number): string => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(Math.abs(amount));
  }, []);

  // Get chart data for Chart.js Bar chart - memoized to prevent recalculation
  const chartData = useMemo(() => {
    if (!analysisData?.response?.data) return null;

    const { data, year1, year2 } = analysisData.response;
    
    return {
      labels: data.map(item => item.month),
      datasets: [
        {
          label: `${year1}`,
          data: data.map(item => item.year1Amount),
          backgroundColor: '#8C7FC6', // Medium purple for year1
          borderColor: '#7A6FBE',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: `${year2}`,
          data: data.map(item => item.year2Amount),
          backgroundColor: '#5E49A8', // Dark purple for year2
          borderColor: '#523F96',
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        }
      ]
    };
  }, [analysisData]);

  // Get summary statistics - memoized to prevent recalculation
  const summaryStats = useMemo(() => {
    if (!analysisData?.response?.data) return null;

    const { data, year1, year2 } = analysisData.response;
    
    const year1Total = data.reduce((sum, item) => sum + item.year1Amount, 0);
    const year2Total = data.reduce((sum, item) => sum + item.year2Amount, 0);
    const difference = year2Total - year1Total;
    const percentageChange = year1Total > 0 ? ((difference / year1Total) * 100) : (year2Total > 0 ? 100 : 0);

    // Single year stats (for backward compatibility)
    const totalAmount = year2Total;
    const averageMonthly = totalAmount / 12;
    const highestMonth = Math.max(...data.map(item => item.year2Amount));
    const highestMonthName = data.find(item => item.year2Amount === highestMonth)?.month || '';
    const monthsWithData = data.filter(item => item.year2Amount > 0).length;

    return {
      // Comparison stats
      year1,
      year2,
      year1Total,
      year2Total,
      difference,
      percentageChange,
      
      // Single year stats
      totalAmount,
      averageMonthly,
      highestMonth,
      highestMonthName,
      monthsWithData,
      year: year2
    };
  }, [analysisData]);

  return {
    // State
    analysisData,
    loading,
    error,
    message,
    currentParams,
    
    // Actions
    fetchAnalysis,
    refreshAnalysis,
    setYears,
    clearError: handleClearError,
    clearMessage: handleClearMessage,
    clearAnalysisData: handleClearAnalysisData,
    
    // Helper functions
    formatAmount,
    getChartData: () => chartData, // Return memoized data
    getSummaryStats: () => summaryStats, // Return memoized data
    
    // Computed values
    hasData: !!analysisData?.response?.data,
    isEmpty: !analysisData?.response?.data?.length,
    monthlyData: analysisData?.response?.data || [],
    years: {
      year1: analysisData?.response?.year1 || currentParams.year1,
      year2: analysisData?.response?.year2 || currentParams.year2
    }
  };
};